import React from "react";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { usePaginationHook } from "../../../../molecules/pagination/usePaginationHook";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { PAGE_NAME, ROUTE } from "../../../../../constants/constants";
import { Input } from "../../../../atoms/input";
import { Table } from "../../../../atoms/table";
import { Divider } from "../../../../atoms/divider";
import { Dropdown } from "../../../../atoms/dropdown";
import { Pagination } from "../../../../molecules/pagination";
import { useCredentialListQuery, useSleetProjectListQuery } from "../../../../../store/credentialsManagement/credentialsSlice";
import { pascalToSnakeCase } from "../../../../../utils";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../../../atoms/icon";

const toolSortFormattedHandler = (sortBy: string) => {
    if (sortBy === "sleetProject") {
        sortBy = "projectName";
    }

    return sortBy;
};

export const CredentialsList = () => {
    const pageClassName = "p-credentialsList";

    const navigate = useNavigate();
    const filterRef = React.useRef<HTMLDivElement>(null);
    const [isShowFilter, setShowFilter] = React.useState(false);
    const [project, setProject] = React.useState<{ label: string; value: string }>({
        label: "Select",
        value: "",
    });
    const [filteredProject, setFilteredProject] = React.useState(project);

    const { data: projects, isLoading: projectLoading } = useSleetProjectListQuery({});

    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: false,
        },
        {
            title: "First Name",
            dataIndex: "firstName",
            isSortable: true,
        },
        {
            title: "Last Name",
            dataIndex: "lastName",
            isSortable: true,
        },
        {
            title: "Email",
            dataIndex: "email",
            isSortable: true,
        },
        {
            title: "Project Name",
            dataIndex: "sleetProject",
            isSortable: true,
        },
        {
            title: "Updated At",
            dataIndex: "updatedAt",
            isSortable: true,
        },
    ];

    const {
        search,
        query,
        pageChangeHandler,
        sortHandler,
        limitChangeHandler,
        searchHandler,
    } = useTableHooks();

    const { data, isLoading, isFetching } = useCredentialListQuery({
        search: query.search,
        limit: query.limit,
        current_page: query.current,
        sort_by: pascalToSnakeCase(toolSortFormattedHandler(query.sortBy)),
        sort_direction: query.sortDirection,
        project_id: filteredProject.value,
    });

    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.CREDENTIAL_LIST}</p>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={(e) => {
                                searchHandler(e);
                                handleFirstPage();
                            }}
                            value={search}
                        />
                    </div>
                    <div className={`${pageClassName}__header__filter`}>
                        <button
                            className={`${pageClassName}__header__button`}
                            onClick={() => navigate(ROUTE.CREDENTIALS_CREATE)}
                        >
                            Create Credential
                        </button>

                        <div className={`${pageClassName}__filter`} ref={filterRef}>
                            <button
                                onClick={() => setShowFilter(!isShowFilter)}
                                className={`${pageClassName}__filter__button`}
                                title="Filter"
                            >
                                <Icon icon="filter" size="primary" />
                            </button>
                            {isShowFilter && (
                                <div className={`${pageClassName}__filter__container`}>
                                    <div className={`${pageClassName}__filter__item`}>
                                        <label>Project</label>
                                        <Dropdown
                                            options={projects?.result?.data.map((project: any) => ({
                                                label: project?.name,
                                                value: project?.id,
                                            }))}
                                            selected={project as { label: string; value: string }}
                                            onClickChange={(e) =>
                                                setProject(e as { label: string; value: string })
                                            }
                                            isBordered
                                            isIcon

                                        />
                                    </div>
                                    <Divider />
                                    <div className={`${pageClassName}__filter__btn`}>
                                        <button
                                            onClick={() => {
                                                setProject({ label: "Select", value: "" });
                                                setFilteredProject({ label: "Select", value: "" });
                                                setShowFilter(false);
                                            }}
                                        >
                                            Reset
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowFilter(false);
                                                setFilteredProject(project);
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading || projectLoading || isFetching}
                        firstLoad={isLoading}
                        sortHandler={sortHandler}
                        minWidth="1000"
                        startIndex={data?.result?.from}
                        sortBy={query.sortBy}
                    />
                </div>
                <Divider />
                <div className={`${pageClassName}__pagination`}>
                    <div className={`${pageClassName}__pagination__limit`}>
                        <p>Display</p>
                        <div className={`${pageClassName}__pagination__dropdown`}>
                            <Dropdown
                                options={[
                                    { label: "20", value: 20 },
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                ]}
                                selected={{ label: query.limit, value: query.limit }}
                                onClickChange={(e) => {
                                    limitChangeHandler(e.value as number);
                                    handleFirstPage();
                                }}
                                isBordered
                                isIcon
                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__pagination__number`}>
                        {data?.result?.total ? (
                            <p>
                                Show {data?.result?.from} -{" "}
                                {data?.result?.from + query.limit - 1 < data?.result?.total
                                    ? data?.result?.from + query.limit - 1
                                    : data?.result?.total}{" "}
                                of {data?.result?.total}
                            </p>
                        ) : (
                            <p>Show 00 - 00 of 00</p>
                        )}
                    </div>
                    <div className={`${pageClassName}__pagination__nav`}>
                        {data?.result?.total > query.limit && (
                            <Pagination
                                totalData={totalData}
                                page={query.current}
                                handleFirstPage={handleFirstPage}
                                handlePrevPage={handlePrevPage}
                                handleLastPage={handleLastPage}
                                handleNextPage={handleNextPage}
                                renderPageNumbers={renderPageNumbers}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
